import React, { useEffect, useState } from "react";
import "./App.css";
import {
  Navbar,
  Nav,
  Row,
  Col,
  Container,
  Spinner
} from 'react-bootstrap';
import { Routes, Route, useNavigate } from 'react-router-dom';
import ReactGA from "react-ga4";
import { useCookieConsent } from "./contexts/CookieConsentContext";
import CookieConsent from "./components/CookieConsent";

import { NewSession } from "./pages/NewSession/NewSession.tsx"
import ShootingEventHome from "./pages/ShootingEventHome/ShootingEventHome.tsx";
import SignIn from "./pages/SignIn/SignIn.tsx";
import { useAuth } from "./contexts/AuthContext.tsx";
import SessionReports from "./pages/SessionReports/SessionReports.tsx";
import AddCompetition from "./pages/AddCompetition/AddCompetition.tsx";
import ManageCompetition from "./pages/ManageCompetition/ManageCompetition.tsx";
import EventRanking from "./pages/ManageEventRanking/ManageEventRanking.tsx";
import ManageClub from "./pages/ManageClub/ManageClub.tsx";
import { useLoading } from "./contexts/LoadingContext";
import CompetitionReport from "./pages/CompetitionReport/CompetitionReport.tsx";
import EventRankingReport from "./pages/EventRankingReport/EventRankingReport.tsx";
import SessionHistory from "./pages/SessionHistory/SessionHistory.tsx";
import Statistics from "./pages/Statistics/Statistics.tsx";
import { UnsavedNavigationProvider, useUnsavedNavigation } from "./contexts/UnsavedNavigationContext.tsx";

function App() {
  return (
    <UnsavedNavigationProvider>
      <AppContent />
    </UnsavedNavigationProvider>
  );
}

function AppContent() {
  const navigate = useNavigate();
  const { isAuthenticated, role, logout } = useAuth();
  const { isLoading } = useLoading();
  const { analyticsConsent } = useCookieConsent();
  const { confirmNavigation } = useUnsavedNavigation();

  const [isNavbarExpanded, setIsNavbarExpanded] = useState(false);
  const [gaInitialized, setGaInitialized] = useState(false);

  useEffect(() => {
    if (analyticsConsent === "accepted" && !gaInitialized) {
      ReactGA.initialize('G-NQFEP8QYQR');
      ReactGA.send("pageview");
      setGaInitialized(true);
    }
  }, [analyticsConsent, gaInitialized]);

  const handleNavigate = async (path: string) => {
    const confirmed = await confirmNavigation();

    if (!confirmed) {
      return;
    }

    setIsNavbarExpanded(false);
    navigate(path);
  };

  const handleLogout = async () => {
    const confirmed = await confirmNavigation();

    if (!confirmed) {
      return;
    }

    setIsNavbarExpanded(false);
    logout();
    navigate('/sign-in');
  };

  return (
    <div className="App">
      <Navbar
        id="navbar"
        bg="dark"
        variant="dark"
        expand="md"
        expanded={isNavbarExpanded}
        onToggle={(expanded) => setIsNavbarExpanded(expanded)}
      >
        <Container fluid className="px-0">
          <Row className="bg-dark w-100 gx-0 flex-wrap flex-lg-nowrap">
            <Col xs="auto" md={2} className="d-flex align-items-center justify-content-start justify-content-lg-center order-1 flex-wrap flex-lg-nowrap">
              <Navbar.Brand
                style={{ cursor: "pointer" }}
                className="ms-2 me-2"
                onClick={() => handleNavigate('/')}
              >
                Target Calculator
              </Navbar.Brand>
              {isAuthenticated && (
                <Navbar.Toggle aria-controls="basic-navbar-nav" />
              )}
            </Col>

            {!isAuthenticated && (
              <>
                <Col className="flex-grow-1 d-lg-none order-2" />
                <Col md={8} className="d-none d-lg-block order-2" />
              </>
            )}

            {isAuthenticated && (
              <Col xs={12} md={8} className="d-block d-lg-flex align-items-lg-center order-3 order-lg-2">
                <Navbar.Collapse id="basic-navbar-nav" className="w-100">
                  <Nav className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center w-100">
                    {role === 'CLIENT' && (
                      <>
                        <Nav.Link className="mb-2 mb-lg-0 me-lg-3 ms-2 ms-lg-0" onClick={() => handleNavigate('/new-session')}>Dodaj sesję</Nav.Link>
                        <Nav.Link className="mb-2 mb-lg-0 me-lg-3 ms-2 ms-lg-0" onClick={() => handleNavigate('/session-reports')}>Moje sesje</Nav.Link>
                        <Nav.Link className="mb-2 mb-lg-0 me-lg-3 ms-2 ms-lg-0" onClick={() => handleNavigate('/statistics')}>Moje statystyki</Nav.Link>
                      </>
                    )}

                    {role === 'ADMIN' && (
                      <>
                        <Nav.Link className="mb-2 mb-lg-0 me-lg-3 ms-2 ms-lg-0" onClick={() => handleNavigate('/add-competition')}>Dodaj zawody</Nav.Link>
                        <Nav.Link className="mb-2 mb-lg-0 me-lg-3 ms-2 ms-lg-0" onClick={() => handleNavigate('/manage-competitions')}>Zarządzaj zawodami</Nav.Link>
                        <Nav.Link className="mb-2 mb-lg-0 me-lg-3 ms-2 ms-lg-0" onClick={() => handleNavigate('/manage-club')}>Zarządzaj klubem</Nav.Link>
                      </>
                    )}
                    <Nav.Link className="d-block d-lg-none mb-2 ms-2" onClick={handleLogout}>Wyloguj się</Nav.Link>
                  </Nav>
                </Navbar.Collapse>
              </Col>
            )}

            {isAuthenticated && (
              <Col xs="auto" md={2} className="order-2 order-lg-3 d-none d-lg-block">
                <Nav className="justify-content-center align-items-center me-4">
                  <Nav.Link onClick={handleLogout}>Wyloguj się</Nav.Link>
                </Nav>
              </Col>
            )}

            {!isAuthenticated && (
              <Col xs="auto" md={2} className="order-3 order-md-3">
                <Nav className="justify-content-center align-items-center me-4">
                  <Nav.Link onClick={() => handleNavigate('/sign-in')}>Zaloguj się</Nav.Link>
                </Nav>
              </Col>
            )}
          </Row>
        </Container>
      </Navbar>

      <Container fluid className="main-container">
        <Row className="flex-grow-1 d-flex">
          <Col md={2} id="leftColumn" />
          <Col md={8} id="middleColumn">
            <Routes>
              <Route path="/" element={<ShootingEventHome />} />
              <Route path="/new-session" element={<NewSession />} />
              <Route path="/sign-in" element={<SignIn />} />
              <Route path="/session-reports" element={<SessionHistory />} />
              <Route path="/session-reports/:sessionId" element={<SessionReports />} />
              <Route path="/statistics" element={<Statistics />} />
              <Route path="/competition-reports/:competitionId" element={<CompetitionReport />} />
              <Route path="/competition-reports/:competitionId/:shootingEventId" element={<EventRankingReport />} />
              <Route path="/add-competition" element={<AddCompetition />} />
              <Route path="/manage-competitions" element={<ManageCompetition />} />
              <Route path="/manage-event-ranking" element={<EventRanking />} />
              <Route path="/manage-club" element={<ManageClub />} />
            </Routes>
          </Col>
          <Col md={2} id="rightColumn" />
        </Row>
      </Container>

      {isLoading && (
        <div className="global-loading-overlay" aria-live="polite" aria-busy="true">
          <Spinner animation="border" variant="light" role="status" />
        </div>
      )}

      <CookieConsent />
    </div>
  );
}


export default App;
